import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { answersMatch } from './sampleMock';
import { pageLabel } from './liveActivity';
import { toDate } from './progress';
import { questionSecondsOf } from './questionStats';
import { topicLabel } from '../data/topics';
import { deviceFields, getClientDevice } from './deviceInfo';

export function geoSnapshot() {
  const snap = {};
  try {
    const cached = sessionStorage.getItem('cfa_geo_v1');
    const geo = cached ? JSON.parse(cached) : null;
    if (geo?.city) snap.geoCity = String(geo.city).slice(0, 80);
    if (geo?.region) snap.geoRegion = String(geo.region).slice(0, 80);
    if (geo?.country) snap.geoCountry = String(geo.country).slice(0, 80);
    if (geo?.countryCode) snap.geoCountryCode = String(geo.countryCode).slice(0, 8);
    if (geo?.timezone) snap.geoTimezone = String(geo.timezone).slice(0, 80);
  } catch {
    /* private mode */
  }
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timezone) snap.timezone = timezone.slice(0, 80);
  } catch {
    /* ignore */
  }
  return snap;
}

const PAGE_DEDUPE_MS = 45_000;
let lastPageVisit = { path: '', at: 0 };

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

export function answerLabel(answer, options = []) {
  if (answer === null || answer === undefined || answer === '') return '—';
  const idx = Number(answer);
  if (!Number.isFinite(idx)) return String(answer);
  const letter = LETTERS[idx] || String(idx + 1);
  const text = options[idx];
  return text ? `${letter} (${String(text).slice(0, 72)}${String(text).length > 72 ? '…' : ''})` : letter;
}

export async function recordPageVisit(pathname) {
  const user = auth.currentUser;
  if (!user?.uid || typeof window === 'undefined') return;
  const path = String(pathname || '/').split('?')[0] || '/';
  if (path.startsWith('/admin')) return;

  const now = Date.now();
  if (lastPageVisit.path === path && now - lastPageVisit.at < PAGE_DEDUPE_MS) return;
  lastPageVisit = { path, at: now };

  try {
    await addDoc(collection(db, 'userProfiles', user.uid, 'pageVisits'), {
      path,
      label: pageLabel(path),
      at: Timestamp.now(),
      ...deviceFields(getClientDevice()),
      ...geoSnapshot(),
    });
  } catch {
    /* quota / offline */
  }
}

export async function recordQuestionEvent({
  topic,
  questionIndex,
  questionTotal,
  questionId,
  questionText,
  answer,
  correct,
  secondsOnItem,
  phase = 'answered',
}) {
  const user = auth.currentUser;
  if (!user?.uid) return;

  try {
    await addDoc(collection(db, 'userProfiles', user.uid, 'questionEvents'), {
      topic: topic || '',
      questionIndex: Number(questionIndex) || 0,
      questionTotal: Number(questionTotal) || 0,
      questionId: questionId != null ? String(questionId) : '',
      questionText: String(questionText || '').slice(0, 220),
      answer: answer === undefined || answer === null ? '' : String(answer),
      correct: correct === true ? true : correct === false ? false : null,
      secondsOnItem: Number.isFinite(Number(secondsOnItem)) ? Number(secondsOnItem) : null,
      phase,
      at: Timestamp.now(),
    });
  } catch {
    /* quota / offline */
  }
}

export function flattenAttemptsToQuestionRows(attempts = []) {
  const rows = [];
  attempts.forEach((attempt) => {
    const questions = attempt.questions || [];
    const answers = attempt.answers || [];
    const startMs = toDate(attempt.startTime)?.getTime();
    let cumulative = 0;
    questions.forEach((question, index) => {
      const seconds = questionSecondsOf(attempt, index) || 0;
      cumulative += seconds;
      const answered =
        answers[index] !== null && answers[index] !== undefined && answers[index] !== '';
      const at =
        startMs != null
          ? new Date(startMs + cumulative * 1000)
          : toDate(attempt.endTime) || null;
      rows.push({
        key: `${attempt.id}-${index}`,
        source: 'attempt',
        at,
        attemptId: attempt.id,
        topic: attempt.topic || '',
        questionIndex: index + 1,
        questionTotal: questions.length,
        questionId: question.id,
        questionText: question.question,
        options: question.options || [],
        answer: answers[index],
        answerLabel: answerLabel(answers[index], question.options),
        correct: answered ? answersMatch(answers[index], question.correctAnswer) : null,
        secondsOnItem: seconds,
        sittingLabel: topicLabel(attempt.topic || '') || attempt.topic || 'Sitting',
      });
    });
  });
  return rows.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
}

export function mergeQuestionLogRows({ events = [], attempts = [] }) {
  const fromAttempts = flattenAttemptsToQuestionRows(attempts);
  const fromEvents = events.map((event) => ({
    key: event.id,
    source: 'live',
    at: toDate(event.at),
    attemptId: '',
    topic: event.topic || '',
    questionIndex: event.questionIndex,
    questionTotal: event.questionTotal,
    questionId: event.questionId,
    questionText: event.questionText,
    options: [],
    answer: event.answer,
    answerLabel:
      event.answer === '' || event.answer == null
        ? '—'
        : answerLabel(event.answer, []),
    correct: event.correct,
    secondsOnItem: event.secondsOnItem,
    sittingLabel: topicLabel(event.topic || '') || event.topic || 'Live session',
    phase: event.phase,
  }));

  const eventKeys = new Set(
    fromEvents.map((row) => `${row.topic}|${row.questionIndex}|${row.at?.getTime() || 0}`)
  );

  const merged = [...fromEvents];
  fromAttempts.forEach((row) => {
    const roughKey = `${row.topic}|${row.questionIndex}|${Math.floor((row.at?.getTime() || 0) / 60000)}`;
    if (eventKeys.has(roughKey)) return;
    merged.push(row);
  });

  return merged.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
}
